import axios from 'axios';
import React from 'react';

import { PortalConfigContext } from 'src/context';
import { useCustomDomains } from 'src/hooks/useCustomDomains';
import { UrlUtils } from 'src/utils';

const HealthCheckPassThreshold = 2;

export interface UsePortalHealthStatusResult {
  isPortalReady: boolean;
  clientExperienceLink: string;
}

export const usePortalHealthStatus = (): UsePortalHealthStatusResult => {
  const { clientExperienceEnabled } = React.useContext(PortalConfigContext);
  const { domains } = useCustomDomains();

  const activeDomain =
    domains.find(
      (x) => x.id !== 'default' && x.domainMetadata?.status === 'SUCCESS',
    ) ?? domains.at(0);
  const clientExperienceLink = `${activeDomain?.domainMetadata?.subdomain}.${activeDomain?.domainMetadata?.domain}`;

  const healthCheckTimer = React.useRef(0);
  const [healthCheckPassed, setHealthCheckPassed] = React.useState(false);
  const healthCheckPassCount = React.useRef(0);

  const isPortalReady = clientExperienceEnabled || healthCheckPassed;

  const runHealthCheck = React.useCallback(async () => {
    try {
      const response = await axios.get(
        UrlUtils.GetFullUrl(`${clientExperienceLink}/health`),
      );
      if (response.status === 200 && response.data === 'Healthy') {
        healthCheckPassCount.current += 1;
        if (healthCheckPassCount.current > HealthCheckPassThreshold) {
          if (healthCheckTimer.current) {
            clearTimeout(healthCheckTimer.current);
          }
          setHealthCheckPassed(true);
          return;
        }
      }
    } catch (e) {
      // do nothing
    }

    healthCheckTimer.current = window.setTimeout(() => {
      void runHealthCheck();
    }, 2000);
  }, [clientExperienceLink]);

  React.useEffect(() => {
    if (!clientExperienceEnabled && activeDomain) {
      void runHealthCheck();
    }
    return () => {
      if (healthCheckTimer.current) {
        clearTimeout(healthCheckTimer.current);
      }
    };
  }, [clientExperienceEnabled, activeDomain, runHealthCheck]);

  React.useEffect(
    () => () => {
      if (healthCheckTimer.current) {
        clearTimeout(healthCheckTimer.current);
      }
    },
    [],
  );

  return { isPortalReady, clientExperienceLink };
};
